import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'reactstrap';
import {
  X, Check, CheckCircle2, Star, ArrowUp, MessageSquare, Tag, Users, ShieldCheck, Lock
} from 'lucide-react';
import { membershipDisplay, launchOffer, PROMO_FIN_ISO } from './membershipDisplay';
import './plans30.scss';

// La fecha límite sale de `membershipDisplay`, que es quien decide si la
// promoción sigue viva: si se repitiera aquí, mover una sin la otra dejaría la
// banda en pantalla con el reloj a cero. La hora es la de Lima (UTC-5), así que
// la cuenta atrás marca lo mismo aquí que en el navegador del cliente.
const [PROMO_ANIO, PROMO_MES, PROMO_DIA] = PROMO_FIN_ISO.split('-');
const PROMO_FIN = `${PROMO_DIA}/${PROMO_MES}/${PROMO_ANIO}`;
const PROMO_FIN_MS = new Date(`${PROMO_FIN_ISO}T23:59:59-05:00`).getTime();

// ==========================================================
// Precios
// ==========================================================
// Un importe por plan y ciclo, y nada más: el ahorro en %, el equivalente
// mensual y el precio tachado salen de aquí. Para cambiar una tarifa basta
// con tocar el número; el resto del modal se recalcula solo.
//
// El ahorro se mide contra pagar mes a mes (`mensual` × meses del ciclo), que
// es lo que el cliente compara.
export const CICLOS = [
  { id: 'mensual', label: 'Mensual', meses: 1, sufijo: '/ mes', pedido: 'mensual' },
  { id: 'trimestral', label: '3 meses', meses: 3, sufijo: '/ 3 meses', pedido: '3 meses' },
  { id: 'semestral', label: '6 meses', meses: 6, sufijo: '/ 6 meses', pedido: '6 meses' },
  { id: 'anual', label: 'Anual', meses: 12, sufijo: '/ año', pedido: 'anual' }
];

const BENEFICIOS_BASICO = [
  ['Contratos menores', 'Seguimiento completo y actualizado'],
  ['Alertas inteligentes por correo', 'Según tus rubros y departamentos recibe alertas diarias'],
  ['Actualización en tiempo real', 'Información actualizada en todo momento'],
  ['Calendario y recordatorios'],
  ['Soporte técnico']
];

const BENEFICIOS_PLUS = [
  ['Convocatorias del SEACE', 'Convocatorias, cronogramas y bases'],
  ['Calendario de cierres y envíos', 'Recordatorios 3 y 1 día antes'],
  ['Soporte técnico prioritario']
];

const BENEFICIOS_PREMIUM = [
  ['Generador de ofertas', 'Prepara los documentos de tu oferta'],
  ['Catálogo de proveedores'],
  ['Descuentos en cursos', 'Capacita a tu equipo para licitar mejor'],
  ['Soporte preferente']
];

export const PLANES = [
  {
    id: 'basico',
    nombre: 'Plan Básico',
    nivel: 'basico',
    para: 'Para empresas que empiezan con contratos menores',
    precios: { mensual: 15, trimestral: 40, semestral: 75, anual: 135 },
    beneficios: BENEFICIOS_BASICO,
    cta: 'Elegir Plan Básico'
  },
  {
    id: 'plus',
    nombre: 'Plan Plus',
    nivel: 'plus',
    para: 'Para empresas que ya licitan por montos mayores',
    precios: { mensual: 25, trimestral: 60, semestral: 110, anual: 200 },
    beneficios: BENEFICIOS_PLUS,
    hereda: 'Básico',
    insignia: 'El más elegido',
    recomendado: true,
    cta: 'Quiero el Plan Plus',
    nota: 'Activación por WhatsApp el mismo día'
  },
  {
    id: 'premium',
    nombre: 'Plan Premium',
    nivel: 'premium',
    para: 'Para equipos que buscan y preparan sus ofertas',
    precios: { mensual: 50, trimestral: 120, semestral: 220, anual: 400 },
    beneficios: BENEFICIOS_PREMIUM,
    hereda: 'Plus',
    insignia: 'Para tu equipo',
    cta: 'Elegir Plan Premium'
  }
];

// ==========================================================
// Cálculos de precio
// ==========================================================
const sinDescuento = (plan, ciclo) => plan.precios.mensual * ciclo.meses;

// Porcentaje redondeado frente a pagar mes a mes. 0 si el ciclo no descuenta.
const ahorroPct = (plan, ciclo) => {
  const lista = sinDescuento(plan, ciclo);
  if (!lista || ciclo.meses === 1) return 0;
  return Math.round((1 - plan.precios[ciclo.id] / lista) * 100);
};

// El mejor descuento de cada ciclo: es el que se anuncia en el selector.
const ahorroMaximo = (ciclo) => Math.max(0, ...PLANES.map((p) => ahorroPct(p, ciclo)));

const descuentoMaximo = Math.max(...CICLOS.map(ahorroMaximo));

// 13.333… → '13.33'; 20 → '20.00'
const porMes = (total, meses) => (total / meses).toFixed(2);

// Días, horas, minutos y segundos que faltan para el fin de la promoción.
const restanteHasta = (fin) => {
  const ms = Math.max(0, fin - Date.now());
  return {
    d: Math.floor(ms / 86400000),
    h: Math.floor(ms / 3600000) % 24,
    m: Math.floor(ms / 60000) % 60,
    s: Math.floor(ms / 1000) % 60
  };
};

const dosCifras = (n) => String(n).padStart(2, '0');

export default function Plans30({ user, getPlanDisplayName, planVisible, isOpen, toggle, onRequest, onContact }) {
  const promo = launchOffer();
  const [cicloId, setCicloId] = useState('trimestral');
  const [cuenta, setCuenta] = useState(() => restanteHasta(PROMO_FIN_MS));

  // El reloj solo corre con el modal abierto y la promoción viva: fuera de ahí
  // no hay nada que mirar y el intervalo sobra.
  useEffect(() => {
    if (!isOpen || !promo.active) return undefined;
    setCuenta(restanteHasta(PROMO_FIN_MS));
    const id = setInterval(() => setCuenta(restanteHasta(PROMO_FIN_MS)), 1000);
    return () => clearInterval(id);
  }, [isOpen, promo.active]);

  const ciclo = useMemo(() => CICLOS.find((c) => c.id === cicloId) || CICLOS[0], [cicloId]);

  // `planVisible` es el plan con el que se compara: para un alumno del plan
  // Curso es el plan al que equivale su grupo, así la tarjeta que se marca es
  // la misma que nombra la insignia de la barra superior.
  const plan = String(planVisible || user?.plan || '').toUpperCase();
  const membership = membershipDisplay(user || {});

  const esActivo = (id) =>
    (id === 'basico' && plan === 'MENOR') ||
    (id === 'plus' && plan === 'PREMIUM') ||
    (id === 'premium' && plan === 'PLUS_PLUS');

  return (
    <Modal
      zIndex={12000}
      isOpen={isOpen}
      toggle={toggle}
      centered
      className="pl30"
      labelledBy="pl30-title"
    >
      {/* ── Cabecera ─────────────────────────────────────── */}
      <div className="pl30-head">
        <div className="pl30-head-txt">
          <h2 id="pl30-title">Elige el plan para impulsar tu empresa</h2>
          <p>
            Tu plan actual: <b>{getPlanDisplayName(plan)}</b>
            {membership.date && (
              <> · {membership.days === 0 ? 'venció' : 'vigente hasta'} el {membership.date}</>
            )}
          </p>
        </div>
        <button type="button" className="pl30-close" onClick={toggle} aria-label="Cerrar modal">
          <X size={18} />
        </button>
      </div>

      {/* ── Promoción: manda arriba, con la fecha en el titular ── */}
      {promo.active && (
        <div className="pl30-promo">
          <div className="pl30-promo-txt">
            <div className="pl30-promo-title">
              <Tag size={19} />
              <span>Promoción por lanzamiento hasta el {PROMO_FIN}</span>
            </div>
            <p>
              Paga por adelantado y ahorra hasta <b>{descuentoMaximo}%</b>. Congelas tu tarifa
              mientras sigas suscrito.
            </p>
          </div>
          <div className="pl30-clock" aria-label={`Quedan ${cuenta.d} días`}>
            {[
              ['DÍAS', cuenta.d],
              ['HORAS', dosCifras(cuenta.h)],
              ['MIN', dosCifras(cuenta.m)],
              ['SEG', dosCifras(cuenta.s)]
            ].map(([etiqueta, valor]) => (
              <div key={etiqueta} className="pl30-clock-box">
                <b>{valor}</b>
                <span>{etiqueta}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Selector de ciclo ────────────────────────────── */}
      <div className="pl30-compare">
        <span className="pl30-compare-label">Compara y elige tu plan</span>
        <div className="pl30-ciclo" role="group" aria-label="Ciclo de pago">
          {CICLOS.map((c) => {
            const dto = ahorroMaximo(c);
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={c.id === cicloId}
                className={c.id === cicloId ? 'is-on' : ''}
                onClick={() => setCicloId(c.id)}
              >
                {c.label}
                {dto > 0 && <em>−{dto}%</em>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Planes ───────────────────────────────────────── */}
      <div className="pl30-grid">
        {PLANES.map((p) => {
          const activo = esActivo(p.id);
          const total = p.precios[ciclo.id];
          const lista = sinDescuento(p, ciclo);
          const pct = ahorroPct(p, ciclo);

          return (
            <article
              key={p.id}
              aria-label={p.nombre}
              className={`pl30-card n-${p.nivel} ${p.recomendado ? 'is-top' : ''}`}
            >
              {p.recomendado && (
                <div className="pl30-flag">
                  <Star size={16} />
                  RECOMENDADO PARA TI
                </div>
              )}

              <div className="pl30-card-head">
                <div className="pl30-card-name">
                  <h3>{p.nombre}</h3>
                  {activo ? (
                    <span className="pl30-badge">Tu plan</span>
                  ) : (
                    p.insignia && (
                      <span className="pl30-badge">
                        {p.nivel === 'premium' && <Users size={13} />}
                        {p.insignia}
                      </span>
                    )
                  )}
                </div>
                <p>{p.para}</p>
              </div>

              <div className="pl30-card-body">
                <div className="pl30-price">
                  <b>S/{total}</b>
                  <span>{ciclo.sufijo}</span>
                </div>

                <div className="pl30-price-sub">
                  {ciclo.meses > 1 ? (
                    <>
                      <span>Equivale a S/{porMes(total, ciclo.meses)} / mes</span>
                      {pct > 0 && <s>S/{lista}</s>}
                    </>
                  ) : (
                    <span>Pago mensual · cancelas cuando quieras</span>
                  )}
                </div>

                {pct > 0 && <div className="pl30-ahorro">Ahorras {pct}%</div>}

                <hr className="pl30-sep" />

                {p.hereda && <div className="pl30-hereda">Todo el Plan {p.hereda}, más:</div>}

                <ul className="pl30-benef">
                  {p.beneficios.map(([titulo, detalle]) => (
                    <li key={titulo}>
                      <Check size={17} />
                      <div>
                        <b>{titulo}</b>
                        {detalle && <small>{detalle}</small>}
                      </div>
                    </li>
                  ))}
                </ul>

                <span className="pl30-spacer" />

                <button
                  type="button"
                  className={`pl30-cta ${activo ? 'is-off' : p.recomendado ? 'is-primary' : ''}`}
                  disabled={activo}
                  onClick={() => onRequest(`${p.nombre} · ${ciclo.pedido} · S/${total}`)}
                >
                  {activo ? (
                    <>
                      <CheckCircle2 size={17} />
                      Plan activo
                    </>
                  ) : (
                    <>
                      <ArrowUp size={17} />
                      {p.cta}
                    </>
                  )}
                </button>

                {p.nota && !activo && <p className="pl30-nota">{p.nota}</p>}
              </div>
            </article>
          );
        })}
      </div>

      {/* ── Pie ──────────────────────────────────────────── */}
      <div className="pl30-foot">
        <span className="pl30-foot-item">
          <ShieldCheck size={17} />
          Cancelas cuando quieras
        </span>
        <span className="pl30-foot-item">
          <Lock size={17} />
          Tus datos no se comparten
        </span>
        <span className="pl30-foot-gap" />
        <span className="pl30-foot-ask">¿No sabes cuál elegir?</span>
        <button type="button" className="pl30-wa" onClick={onContact}>
          <MessageSquare size={16} />
          Escríbenos por WhatsApp
        </button>
      </div>
    </Modal>
  );
}
